import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ILegalClient,
  ILegalClientFullObject,
} from '../../Interfaces/ILegalClient';
import { ILegalClientContactInformationType } from '../../Interfaces/ILegalClientContactInformation';
import { ILegalClientLog } from '../../Interfaces/ILegalClientLog';
import { ILegalClientType } from '../../Interfaces/ILegalClientType';
import { customFetcher } from '../useFetch';

//////////////////// LegalClients
export const useGetLegalClients = () => {
  const {
    data: legalClients,
    isLoading: isInitialLoadingLegalClients,
    isRefetching: isRefetchingLegalClients,
    refetch: refetchLegalClients,
  } = useQuery<ILegalClient[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalClients'],
    queryFn: () => customFetcher({ endpoint: '/legalclient' }),
  });

  return {
    legalClients,
    isLoadingLegalClients: isInitialLoadingLegalClients,
    isRefetchingLegalClients,
    refetchLegalClients,
  };
};

export const useGetLegalClientFullObject = (idLegalClient: number) => {
  const {
    data: legalClientFullObject,
    isFetching: isFetchingLegalClientFullObject,
    refetch: refetchLegalClientFullObject,
  } = useQuery<ILegalClientFullObject>({
    queryKey: ['legalclient/fullObject', idLegalClient],
    queryFn: () =>
      customFetcher({ endpoint: `/legalclient/fullObject/${idLegalClient}` }),
  });

  return {
    legalClientFullObject,
    isFetchingLegalClientFullObject,
    refetchLegalClientFullObject,
  };
};

export const useCreateLegalClientFullObject = () => {
  const {
    mutateAsync: createLegalClientFullObject,
    isPending: isPendingCreateLegalClientFullObject,
  } = useMutation({
    mutationFn: (legalClient: ILegalClientFullObject) =>
      customFetcher({
        endpoint: '/legalclient/fullObject',
        method: 'POST',
        body: legalClient,
      }),
  });

  return {
    createLegalClientFullObject,
    isPendingCreateLegalClientFullObject,
  };
};

export const useUpdateLegalClientFullObject = () => {
  const {
    mutateAsync: updateLegalClientFullObject,
    isPending: isPendingUpdateLegalClientFullObject,
  } = useMutation({
    mutationFn: (legalClient: ILegalClientFullObject) =>
      customFetcher({
        endpoint: '/legalclient/fullObject',
        method: 'PUT',
        body: legalClient,
      }),
  });
  return {
    updateLegalClientFullObject,
    isPendingUpdateLegalClientFullObject,
  };
};

export const useGetLegalClientTypes = () => {
  const {
    data: legalClientTypes,
    isLoading: isLoadingLegalClientTypes,
    refetch: refetchLegalClientTypes,
  } = useQuery<ILegalClientType[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalclienttype'],
    queryFn: () => customFetcher({ endpoint: '/legalclienttype' }),
  });

  return {
    legalClientTypes,
    isLoadingLegalClientTypes,
    refetchLegalClientTypes,
  };
};

export const useGetLegalClientContactInformationTypes = () => {
  const {
    data: contactInformationTypes,
    isLoading: isLoadingContactInformationTypes,
    refetch: refetchContactInformationTypes,
  } = useQuery<ILegalClientContactInformationType[]>({
    refetchOnWindowFocus: false,
    queryKey: ['contactinformationtype'],
    queryFn: () => customFetcher({ endpoint: '/contactinformationtype' }),
  });

  return {
    contactInformationTypes,
    isLoadingContactInformationTypes,
    refetchContactInformationTypes,
  };
};

export const useDeleteLegalClient = () => {
  const {
    mutateAsync: deleteLegalClient,
    isPending: isPendingDeleteLegalClient,
  } = useMutation({
    mutationFn: (id: number) =>
      customFetcher({
        endpoint: `/legalclient/${id}`,
        method: 'DELETE',
      }),
  });

  return {
    deleteLegalClient,
    isPendingDeleteLegalClient,
  };
};

export const useGetLegalClientLogs = () => {
  const {
    data: legalClientLogs,
    isLoading: isLoadingLegalClientLogs,
    refetch: refetchLegalClientLogs,
  } = useQuery<ILegalClientLog[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalclientlog'],
    queryFn: () => customFetcher({ endpoint: '/legalclientlog' }),
  });

  return {
    legalClientLogs,
    isLoadingLegalClientLogs,
    refetchLegalClientLogs,
  };
};

export const useGetLegalClientLogsByLegalClient = (idLegalClient: number) => {
  const {
    data: legalClientLogs,
    isLoading: isLoadingLegalClientLogs,
    refetch: refetchLegalClientLogs,
  } = useQuery<ILegalClientLog[]>({
    queryKey: ['legalclient/log', idLegalClient],
    queryFn: () =>
      customFetcher({ endpoint: `/legalclient/log/${idLegalClient}` }),
  });

  return {
    legalClientLogs,
    isLoadingLegalClientLogs,
    refetchLegalClientLogs,
  };
};
